<template>
  <v-row justify="center">
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="900"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          flat
          height="80"
          max-height="80"
        >
          <v-avatar>
            <v-icon
              color="brand-primary"
              x-large
            >
              mdi-crosshairs-gps
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <template v-if="!!model?.Id">
              {{ $t('Account.Addresses.Button.Change.Title') }}
            </template>
            <template v-else>
              {{ $t('Account.Addresses.Button.Add.Title') }}
            </template>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            class="grey lighten-3 ma-0"
            color="black"
            icon
            @click="closeDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text
          ref="formContainer"
          class="pa-lg-4 pa-2"
        >
          <v-form
            v-if="model"
            ref="form"
            @submit.prevent
          >
            <v-container
              fluid
              pa-0
            >
              <v-row dense>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-map-autocomplete-service
                    v-model="selectedLocation"
                    :address-marker="markerAddress"
                    :country="locationDeliveryCountriesStr"
                    :disabled="model.useCustomAddress"
                    :geocode-access-token="geocodeProvider.Key"
                    :geocode-provider="geocodeProvider.Name"
                    :geolocate="true"
                    :hint="$t('Checkout.Details.DeliveryDetails.Field.Autocomplete.Hint')"
                    :map="isVisible && (!isCustomAddress(address) && !model.useCustomAddress)"
                    :map-center="mapCenter"
                    :maps-access-token="interactiveMapsProvider.Key"
                    :maps-provider="interactiveMapsProvider.Name"
                    :placeholder="$t('Checkout.Details.DeliveryDetails.Field.Autocomplete.Label')"
                    :rules="model.validator.vuetifyFormFieldRules('AutoComplete')"
                    :types="['geocode']"
                    color="brand-primary"
                    display-field="Name"
                    map-width="100%"
                    @place-changed="(placeObj) => setAddressFromPlace(placeObj, AppConfig.Marketplace.DeliveryServiceType === ServiceTypeEnum.STORE)"
                  />

                  <div
                    v-if="isCustomAddress(address) || model.useCustomAddress"
                    class="mt-4"
                  >
                    <v-img
                      :height="acceptCustomAddresses ? 320 : 355"
                      position="center center"
                      src="/img/map/map-placeholder.png"
                      style="border: 1px solid #e2e2e2;"
                      width="100%"
                    />
                  </div>

                  <div v-if="acceptCustomAddresses">
                    <v-switch
                      v-model="model.useCustomAddress"
                      :label="$t('Account.Addresses.UseCustom')"
                      class="pt-0 mt-4"
                      color="success"
                      hide-details
                      inset
                    />
                  </div>
                </v-col>

                <v-col
                  cols="12"
                  md="6"
                >
                  <v-container
                    fluid
                    pa-0
                  >
                    <v-row dense>
                      <template v-if="!model.useCustomAddress && !acceptLatLngAddresses">
                        <v-col cols="12">
                          <v-text-field
                            v-model="model.Nickname"
                            :disabled="!model.Formatted"
                            :hint="$t('Account.Addresses.Field.Nickname.Hint')"
                            :label="$t('Account.Addresses.Field.Nickname.Label')"
                            :placeholder="$t('Account.Addresses.Field.Nickname.Placeholder')"
                            :rules="model.validator.vuetifyFormFieldRules('Nickname')"
                            clearable
                            filled
                            hide-details="auto"
                            prepend-inner-icon="mdi-label"
                            type="text"
                          />
                        </v-col>

                        <v-col cols="7">
                          <v-map-autocomplete-service
                            v-model="selectedCity"
                            :country="locationDeliveryCountriesStr"
                            :disabled="!model.Formatted || !isVisible"
                            :geocode-access-token="geocodeProvider.Key"
                            :geocode-provider="geocodeProvider.Name"
                            :hint="$t('Account.Addresses.Field.City.Hint')"
                            :label="$t('Account.Addresses.Field.City.Label')"
                            :placeholder="$t('Account.Addresses.Field.City.Placeholder')"
                            :rules="model.validator.vuetifyFormFieldRules('City')"
                            :types="['city']"
                            color="brand-primary"
                            display-field="Properties.City"
                            @place-changed="(placeObj) => setAddressFromPlace(placeObj,AppConfig.Marketplace.DeliveryServiceType === ServiceTypeEnum.STORE)"
                          />
                        </v-col>

                        <v-col cols="5">
                          <v-text-field
                            v-model="model.Postcode"
                            :disabled="!model.Formatted"
                            :hint="$t('Account.Addresses.Field.Postcode.Hint')"
                            :label="$t('Account.Addresses.Field.Postcode.Label')"
                            :placeholder="$t('Account.Addresses.Field.Postcode.Placeholder')"
                            :rules="model.validator.vuetifyFormFieldRules('Postcode')"
                            clearable
                            filled
                            hide-details="auto"
                            type="text"
                          />
                        </v-col>

                        <v-col cols="7">
                          <v-map-autocomplete-service
                            v-model="selectedStreet"
                            :country="locationDeliveryCountriesStr"
                            :disabled="!model.Formatted || !isVisible"
                            :geocode-access-token="geocodeProvider.Key"
                            :geocode-provider="geocodeProvider.Name"
                            :hint="$t('Account.Addresses.Field.Street.Hint')"
                            :label="$t('Account.Addresses.Field.Street.Label')"
                            :placeholder="$t('Account.Addresses.Field.Street.Placeholder')"
                            :rules="model.validator.vuetifyFormFieldRules('Street')"
                            :types="['address']"
                            color="brand-primary"
                            display-field="Properties.Street"
                            @place-changed="(placeObj) => setAddressFromPlace(placeObj,AppConfig.Marketplace.DeliveryServiceType === ServiceTypeEnum.STORE)"
                          />
                        </v-col>

                        <v-col cols="5">
                          <v-text-field
                            v-model="model.Number"
                            :disabled="!model.Formatted"
                            :hint="$t('Account.Addresses.Field.Number.Hint')"
                            :label="$t('Account.Addresses.Field.Number.Label')"
                            :placeholder="$t('Account.Addresses.Field.Number.Placeholder')"
                            :rules="model.validator.vuetifyFormFieldRules('Number')"
                            clearable
                            filled
                            hide-details="auto"
                            type="text"
                          />
                        </v-col>

                        <v-col cols="7">
                          <v-text-field
                            v-model="model.DoorBell"
                            :disabled="!model.Formatted"
                            :hint="$t('Account.Addresses.Field.DoorBell.Hint')"
                            :label="$t('Account.Addresses.Field.DoorBell.Label')"
                            :placeholder="$t('Account.Addresses.Field.DoorBell.Placeholder')"
                            :rules="model.validator.vuetifyFormFieldRules('DoorBell')"
                            clearable
                            filled
                            hide-details="auto"
                            prepend-inner-icon="mdi-bell"
                            type="text"
                          />
                        </v-col>

                        <v-col cols="5">
                          <v-text-field
                            v-model="model.Floor"
                            :disabled="!model.Formatted"
                            :hint="$t('Account.Addresses.Field.Floor.Hint')"
                            :label="$t('Account.Addresses.Field.Floor.Label')"
                            :placeholder="$t('Account.Addresses.Field.Floor.Placeholder')"
                            :rules="model.validator.vuetifyFormFieldRules('Floor')"
                            clearable
                            filled
                            hide-details="auto"
                            type="text"
                          />
                        </v-col>

                        <v-col cols="12">
                          <v-textarea
                            v-model="model.Comment"
                            :disabled="!model.Formatted"
                            :hint="$t('Account.Addresses.Field.Comment.Hint')"
                            :label="$t('Account.Addresses.Field.Comment.Label')"
                            :placeholder="$t('Account.Addresses.Field.Comment.Placeholder')"
                            :rules="model.validator.vuetifyFormFieldRules('Comment')"
                            auto-grow
                            clearable
                            filled
                            hide-details="auto"
                            prepend-inner-icon="mdi-message-reply-text"
                            rows="3"
                          />
                        </v-col>

                        <v-col cols="12">
                          <v-checkbox
                            v-model="model.IsDefault"
                            :disabled="!model.Formatted"
                            :label="$t('Account.Addresses.Field.IsDefault.Label')"
                            :rules="model.validator.vuetifyFormFieldRules('IsDefault')"
                            class="mt-0"
                            hide-details
                          />
                        </v-col>
                      </template>

                      <template v-else>
                        <v-col cols="12">
                          <v-text-field
                            v-model="model.Nickname"
                            :hint="$t('Account.Addresses.Field.Nickname.Hint')"
                            :label="$t('Account.Addresses.Field.Nickname.Label')"
                            :placeholder="$t('Account.Addresses.Field.Nickname.Placeholder')"
                            :rules="model.validator.vuetifyFormFieldRules('Nickname')"
                            clearable
                            filled
                            hide-details="auto"
                            prepend-inner-icon="mdi-label"
                            type="text"
                          />
                        </v-col>

                        <v-col cols="7">
                          <v-text-field
                            v-model="model.City"
                            :hint="$t('Account.Addresses.Field.City.Hint')"
                            :label="$t('Account.Addresses.Field.City.Label')"
                            :placeholder="$t('Account.Addresses.Field.City.Placeholder')"
                            :rules="acceptLatLngAddresses ? [] : model.validator.vuetifyFormFieldRules('City')"
                            clearable
                            filled
                            hide-details="auto"
                            prepend-inner-icon="mdi-map-marker"
                            type="text"
                          />
                        </v-col>

                        <v-col cols="5">
                          <v-text-field
                            v-model="model.Postcode"
                            :hint="$t('Account.Addresses.Field.Postcode.Hint')"
                            :label="$t('Account.Addresses.Field.Postcode.Label')"
                            :placeholder="$t('Account.Addresses.Field.Postcode.Placeholder')"
                            :rules="acceptLatLngAddresses ? [] : model.validator.vuetifyFormFieldRules('Postcode')"
                            clearable
                            filled
                            hide-details="auto"
                            type="text"
                          />
                        </v-col>

                        <v-col cols="7">
                          <v-text-field
                            v-model="model.Street"
                            :hint="$t('Account.Addresses.Field.Street.Hint')"
                            :label="$t('Account.Addresses.Field.Street.Label')"
                            :placeholder="$t('Account.Addresses.Field.Street.Placeholder')"
                            :rules="acceptLatLngAddresses ? [] : model.validator.vuetifyFormFieldRules('Street')"
                            clearable
                            filled
                            hide-details="auto"
                            prepend-inner-icon="mdi-map-marker"
                            type="text"
                          />
                        </v-col>

                        <v-col cols="5">
                          <v-text-field
                            v-model="model.Number"
                            :hint="$t('Account.Addresses.Field.Number.Hint')"
                            :label="$t('Account.Addresses.Field.Number.Label')"
                            :placeholder="$t('Account.Addresses.Field.Number.Placeholder')"
                            :rules="acceptLatLngAddresses ? [] : model.validator.vuetifyFormFieldRules('Number')"
                            clearable
                            filled
                            hide-details="auto"
                            type="text"
                          />
                        </v-col>

                        <v-col cols="7">
                          <v-text-field
                            v-model="model.DoorBell"
                            :hint="$t('Account.Addresses.Field.DoorBell.Hint')"
                            :label="$t('Account.Addresses.Field.DoorBell.Label')"
                            :placeholder="$t('Account.Addresses.Field.DoorBell.Placeholder')"
                            :rules="acceptLatLngAddresses ? [] : model.validator.vuetifyFormFieldRules('DoorBell')"
                            clearable
                            filled
                            hide-details="auto"
                            prepend-inner-icon="mdi-map-marker"
                            type="text"
                          />
                        </v-col>

                        <v-col cols="5">
                          <v-text-field
                            v-model="model.Floor"
                            :hint="$t('Account.Addresses.Field.Floor.Hint')"
                            :label="$t('Account.Addresses.Field.Floor.Label')"
                            :placeholder="$t('Account.Addresses.Field.Floor.Placeholder')"
                            :rules="acceptLatLngAddresses ? [] : model.validator.vuetifyFormFieldRules('Floor')"
                            clearable
                            filled
                            hide-details="auto"
                            type="text"
                          />
                        </v-col>

                        <v-col cols="12">
                          <v-textarea
                            v-model="model.Comment"
                            :hint="$t('Account.Addresses.Field.Comment.Hint')"
                            :label="$t('Account.Addresses.Field.Comment.Label')"
                            :placeholder="$t('Account.Addresses.Field.Comment.Placeholder')"
                            :rules="model.validator.vuetifyFormFieldRules('Comment')"
                            auto-grow
                            clearable
                            filled
                            hide-details="auto"
                            prepend-inner-icon="mdi-message-reply-text"
                            rows="3"
                          />
                        </v-col>

                        <v-col cols="12">
                          <v-checkbox
                            v-model="model.IsDefault"
                            :label="$t('Account.Addresses.Field.IsDefault.Label')"
                            :rules="model.validator.vuetifyFormFieldRules('IsDefault')"
                            class="mt-0"
                            hide-details
                          />
                        </v-col>
                      </template>
                    </v-row>
                  </v-container>
                </v-col>

                <v-col
                  v-if="showGenericError && genericError"
                  cols="12"
                >
                  <v-alert
                    border="left"
                    class="my-2"
                    dense
                    text
                    type="error"
                  >
                    {{ genericError }}
                  </v-alert>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />

          <v-btn
            :disabled="isFormSaving"
            :loading="isFormSaving"
            color="brand-primary brand-green-dark--text"
            depressed
            large
            @click="onSaveClick"
          >
            <span class="brand-text-title">{{ $t('Common.Button.Save') }}</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>

import AccountAddEditAddressDialogMixin from '@/theme/default/components/account/AccountAddEditAddressDialog.js'

export default {
  name  : 'AccountAddEditAddressDialog',
  mixins: [AccountAddEditAddressDialogMixin]
}
</script>

<style scoped src="@/theme/default/components/account/AccountAddEditAddressDialog.css"></style>

<style scoped>
</style>

